import { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '../cn.js';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  /** Extra line under the message. */
  description?: string;
  /** Milliseconds before it auto-dismisses. `0` disables auto-dismiss. Default 4000. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
  variant: ToastVariant;
  message: string;
}

/**
 * Module-level store, not React context. Every other piece of feedback UI in this app (dialogs,
 * mutations) fires far from wherever `<Toaster />` happens to be mounted — a context would force
 * every caller to be a descendant of the provider and to thread it through. One `<Toaster />` per
 * app (mounted once, see AppShell/main.tsx) subscribes here; `toast.success(...)` etc. can be
 * called from anywhere, including outside any component (a query's `onError`, a plain callback).
 */
let items: ToastItem[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ToastItem[] {
  return items;
}

let nextId = 0;

function push(variant: ToastVariant, message: string, options?: ToastOptions): string {
  const id = `toast-${++nextId}`;
  items = [...items, { id, variant, message, ...options }];
  emit();
  return id;
}

function dismiss(id: string): void {
  items = items.filter((item) => item.id !== id);
  emit();
}

/**
 * Fire from anywhere in the app — a mutation's `onSuccess`/`onError`, a form handler, a plain
 * `catch`. Rendered by the single `<Toaster />` mounted at the app root.
 */
export const toast = {
  success: (message: string, options?: ToastOptions) => push('success', message, options),
  error: (message: string, options?: ToastOptions) => push('error', message, options),
  info: (message: string, options?: ToastOptions) => push('info', message, options),
  warning: (message: string, options?: ToastOptions) => push('warning', message, options),
  dismiss,
};

const VARIANT_ICON: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const VARIANT_ICON_CLASSES: Record<ToastVariant, string> = {
  success: 'text-success bg-success-subtle',
  error: 'text-danger bg-danger-subtle',
  info: 'text-info bg-info-subtle',
  warning: 'text-warning bg-warning-subtle',
};

const DEFAULT_DURATION = 4000;

function ToastCard({ item }: { item: ToastItem }) {
  const duration = item.duration ?? DEFAULT_DURATION;
  const Icon = VARIANT_ICON[item.variant];

  useEffect(() => {
    if (duration <= 0) return;
    const timer = window.setTimeout(() => dismiss(item.id), duration);
    return () => window.clearTimeout(timer);
  }, [item.id, duration]);

  return (
    <div
      role="status"
      className="animate-in fade-in slide-in-from-bottom-2 flex w-80 items-start gap-3 rounded-lg border border-border bg-surface-raised p-3.5 shadow-lg"
    >
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          VARIANT_ICON_CLASSES[item.variant],
        )}
        aria-hidden="true"
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium text-fg">{item.message}</p>
        {item.description && (
          <p className="mt-0.5 text-xs text-fg-muted">{item.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(item.id)}
        aria-label="Dismiss notification"
        className="shrink-0 rounded-md p-1 text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg cursor-pointer"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/**
 * Mount once, at the app root (see AppShell.tsx) — every `toast.*(...)` call anywhere in the app
 * renders here. Bottom-right, stacked newest-on-top; each card auto-dismisses independently.
 */
export function Toaster() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  if (current.length === 0) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col-reverse items-end gap-2 px-4 sm:inset-x-auto sm:right-4"
      aria-live="polite"
    >
      {current.map((item) => (
        <div key={item.id} className="pointer-events-auto">
          <ToastCard item={item} />
        </div>
      ))}
    </div>,
    document.body,
  );
}
